import { RichTextEditorSideEffects } from '@modules/common/components/RichTextEditor/RichTextEditorSideEffects';
import { RichTextEditorToolbar } from '@modules/common/components/RichTextEditor/RichTextEditorToolbar';
import { richTextEditorContainerSX } from '@modules/common/components/RichTextEditor/constants';
import { richTextEditorExtensions } from '@modules/common/components/RichTextEditor/richTextEditorExtensions';
import { Box } from '@mui/material';
import { BubbleMenu, EditorOptions, EditorProvider } from '@tiptap/react';
import { Ref, forwardRef, useRef } from 'react';

export interface RichTextEditorProps extends Partial<EditorOptions> {
  shouldFocus?: boolean;
}

export const RichTextEditor = forwardRef(function (
  {
    onUpdate,
    content,
    onBlur,
    onFocus,
    editable,
    shouldFocus,
  }: RichTextEditorProps,
  ref: Ref<HTMLDivElement>,
) {
  const shouldForceCloseBubbleMenuRef = useRef<boolean | null>(null);

  return (
    <Box
      ref={ref}
      spellCheck="false"
      aria-expanded="false"
      sx={richTextEditorContainerSX}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <EditorProvider
        extensions={richTextEditorExtensions}
        content={content}
        onUpdate={onUpdate}
        onFocus={onFocus}
        onBlur={onBlur}
        editorProps={{
          handleDOMEvents: {
            drop: (_view, ev) => {
              ev.preventDefault();
              return false;
            },
          },
        }}
      >
        <BubbleMenu
          updateDelay={50}
          // Workaround to control the menu visibility to make it work with the link/typography formatting dropdowns
          shouldShow={(inputs) => {
            if (shouldForceCloseBubbleMenuRef.current !== null) {
              return !shouldForceCloseBubbleMenuRef.current;
            }

            return (
              inputs.editor.options.editable && !inputs.state.selection.empty
            );
          }}
          tippyOptions={{
            popperOptions: {
              strategy: `fixed`,
              modifiers: [
                {
                  name: `computeStyles`,
                  options: {
                    adaptive: false,
                    gpuAcceleration: false,
                  },
                },
              ],
            },
          }}
        >
          <RichTextEditorToolbar
            forceCloseBubbleMenu={() => {
              shouldForceCloseBubbleMenuRef.current = true;

              setTimeout(() => {
                shouldForceCloseBubbleMenuRef.current = null;
              }, 200);
            }}
          />
        </BubbleMenu>
        <RichTextEditorSideEffects
          isEditable={editable}
          shouldFocus={shouldFocus}
        />
      </EditorProvider>
    </Box>
  );
});
